import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TrendingUp, TrendingDown, X, Crown } from 'lucide-react';
import { hideNativeTabBar, showNativeTabBar } from '../../services/api';
import { getCoinLogoPath } from '../../utils/coinLogo';
import './RankingModal.css';

// 백엔드 응답 스키마:
// { timeFrame: "1d"|"8h", rankings: [{ ticker, nameKr, nameEn, iconUrl, score, runAt }] }
const RankingModal = ({ isOpen, onClose, rankingData, onStockClick }) => {
  useEffect(() => {
    if (isOpen) hideNativeTabBar();
    else showNativeTabBar();
    return () => showNativeTabBar();
  }, [isOpen]);

  const rankings = rankingData?.rankings || [];

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" fill="currentColor" />;
    if (rank === 2) return <Crown className="w-5 h-5 text-blue-200" fill="currentColor" />;
    if (rank === 3) return <Crown className="w-5 h-5 text-yellow-200" fill="currentColor" />;
    return null;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="ranking-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className="far-modal-root fixed inset-0 z-[10000] bg-white flex flex-col overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
            {/* 헤더 */}
            <div className="far-header">
              <div className="far-header-left">
                <span className="far-header-report-type">AI RANKING</span>
                <div className="far-header-title-row">
                  <span className="far-header-title">오늘의 AI 코인 랭킹</span>
                </div>
              </div>
              <button onClick={onClose} className="far-close-btn">
                <X size={15} />
              </button>
            </div>

            {/* 랭킹 리스트 */}
            <div className="ranking-modal-body">
              <div className="space-y-3 md:space-y-3">
                {rankings.map((coin, index) => {
                  const logoSrc = getCoinLogoPath(coin);
                  return (
                    <div
                      key={coin.ticker}
                      className={`bg-white/80 backdrop-blur-sm rounded-2xl hover:shadow-xl transition-all duration-300 p-3 md:p-5 border cursor-pointer ${
                        index < 3
                          ? 'border-blue-200 shadow-lg shadow-blue-100/50'
                          : 'border-slate-200 shadow-md hover:border-blue-200'
                      }`}
                      onClick={() => {
                        onClose();
                        onStockClick(coin.ticker);
                      }}
                    >
                      {/* 모바일 */}
                      <div className="md:hidden flex items-center gap-2">
                        <div className="flex-shrink-0">
                          {index < 3 ? getRankIcon(index + 1) : <span className="text-lg text-gray-400">#{index + 1}</span>}
                        </div>

                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 shadow-md overflow-hidden">
                            {logoSrc ? (
                              <img
                                src={logoSrc}
                                alt={coin.ticker}
                                className="w-full h-full object-contain p-1.5"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span
                              className="text-sm text-gray-400 w-full h-full items-center justify-center"
                              style={{ display: logoSrc ? 'none' : 'flex' }}
                            >
                              {coin.ticker.charAt(0)}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-bold text-gray-900 truncate">{coin.ticker}</h3>
                          <p className="text-xs text-gray-500 truncate">{coin.nameKr || coin.nameEn}</p>
                        </div>

                        <div className="flex-shrink-0 text-right">
                          <div className="text-lg font-bold text-gray-900">{coin.score}점</div>
                        </div>
                      </div>

                      {/* 데스크톱 */}
                      <div className="hidden md:flex items-center gap-4">
                        <div className="flex items-center justify-center min-w-[50px]">
                          {index < 3 ? (
                            <div className="flex flex-col items-center">
                              {getRankIcon(index + 1)}
                              <span className="text-xs text-gray-500 mt-1">#{index + 1}</span>
                            </div>
                          ) : (
                            <div className="text-center">
                              <span className="text-xl text-gray-400">#{index + 1}</span>
                            </div>
                          )}
                        </div>

                        <div className="relative">
                          <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gray-100 shadow-lg overflow-hidden">
                            {logoSrc ? (
                              <img
                                src={logoSrc}
                                alt={coin.ticker}
                                className="w-full h-full object-contain p-2"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <span
                              className="text-xl text-gray-400 w-full h-full items-center justify-center"
                              style={{ display: logoSrc ? 'none' : 'flex' }}
                            >
                              {coin.ticker.charAt(0)}
                            </span>
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="text-xl font-bold text-gray-900">{coin.ticker}</h3>
                          <p className="text-sm text-gray-600 truncate">{coin.nameKr || coin.nameEn}</p>
                        </div>

                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900 mb-1">{coin.score}점</div>
                          <div
                            className={`flex items-center justify-end gap-1 text-sm ${
                              coin.score >= 50 ? 'text-green-500' : 'text-red-500'
                            }`}
                          >
                            {coin.score >= 50 ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : (
                              <TrendingDown className="w-4 h-4" />
                            )}
                            <span>AI 분석</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <p className="text-xs text-gray-500 text-center">
                AI 점수는 기술적 분석, 펀더멘털, 뉴스, 시장 심리 등을 종합한 투자 참고 지표입니다
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RankingModal;
